import {toast} from 'react-toastify';
import {observer} from 'mobx-react-lite';
import React, {FC, useState} from 'react';
import {formatDistanceToNow} from 'date-fns';
import {CheckCircleIcon, ExclamationTriangleIcon} from '@heroicons/react/24/outline';
import {IWebhook, TWebhookAuthMethod, VerdocsEndpoint, WEBHOOK_EVENTS, rotateWebhookSecret} from '@verdocs/js-sdk';
import {Badge, Button, Card, EmptyState, FlexFill, HelpDialog, PageTitle, SectionTitle, SegmentedControl, Spinner} from '../../Components';
import {EditWebhookDialog, OkCancelDialog, WebhookSecretDialog} from '../../Dialogs';
import {useOrganizationWebhook} from '../../react-sdk/queries';
import {WebhookDeliveryLog} from './Components';

const AUTH_LABEL: Record<TWebhookAuthMethod, string> = {
  none: 'No authentication',
  hmac: 'HMAC signature',
  client_credentials: 'OAuth client credentials',
};

const hostOf = (url: string) => {
  try {
    return new URL(url).host;
  } catch {
    return '';
  }
};

const ago = (date: string) => formatDistanceToNow(new Date(date), {addSuffix: true});

const Code: FC<{children: React.ReactNode}> = ({children}) => (
  <span className="whitespace-nowrap font-mono text-[12px] text-[#33364b]">{children}</span>
);

const EndpointCard: FC<{webhook: IWebhook; onEdit: () => void}> = ({webhook, onEdit}) => {
  const {url, active, auth_method, status, last_success, last_failure} = webhook;
  const failing = !!last_failure && (!last_success || new Date(last_failure) > new Date(last_success));
  const enabledEvents = WEBHOOK_EVENTS.filter((event) => webhook.events?.[event]).length;

  return (
    <Card className={`mb-4 ${failing ? 'border-[#e7c9b4]' : ''}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[15px] font-medium text-[#33364b]">{hostOf(url) || 'Endpoint'}</span>
            {active ? <Badge tone="ok">Active</Badge> : <Badge tone="muted">Paused</Badge>}
            {failing && <Badge tone="warn">Failing</Badge>}
          </div>
          {url && <div className="mt-1 truncate font-mono text-[12px] text-gray-500">{url}</div>}
          <div className="mt-1.5 text-[12px] text-gray-500">
            {AUTH_LABEL[auth_method] || auth_method} · {enabledEvents} of {WEBHOOK_EVENTS.length} events
          </div>
        </div>

        <Button size="small" variant="secondary" label="Edit" onClick={onEdit} />
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-[#f2f3f5] pt-3 text-[12px]">
        <span className="flex items-center gap-1.5" title={last_success ? new Date(last_success).toLocaleString() : undefined}>
          <CheckCircleIcon className={`h-4 w-4 ${last_success ? 'text-[#1f7a4c]' : 'text-gray-300'}`} />
          <span className="text-gray-500">{last_success ? `Last success ${ago(last_success)}` : 'No successful delivery yet'}</span>
        </span>
        {last_failure && (
          <span className="flex items-center gap-1.5" title={new Date(last_failure).toLocaleString()}>
            <ExclamationTriangleIcon className={`h-4 w-4 ${failing ? 'text-[#c2410c]' : 'text-gray-300'}`} />
            <span className={failing ? 'text-[#c2410c]' : 'text-gray-500'}>Last failure {ago(last_failure)}</span>
          </span>
        )}
      </div>

      {failing && status && (
        <div className="mt-3 flex items-start gap-2 rounded-md bg-[#fdf1e3] p-3">
          <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#95591a]" />
          <div className="min-w-0 break-words text-[12px] text-[#95591a]">
            The last delivery failed: <span className="font-medium">{status}</span>
          </div>
        </div>
      )}
    </Card>
  );
};

export const Webhooks: FC = observer(() => {
  const webhookQuery = useOrganizationWebhook();
  const [tab, setTab] = useState('endpoint');
  const [editing, setEditing] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [confirmingReset, setConfirmingReset] = useState(false);
  const [revealed, setRevealed] = useState<{secret: string; rotated: boolean} | null>(null);

  // The persisted cache may hold another org's webhook
  const loading = !webhookQuery.isFetchedAfterMount;
  const notFound = (webhookQuery.error as any)?.response?.status === 404;
  const webhook = webhookQuery.isError ? null : webhookQuery.data || null;

  const refetch = () => webhookQuery.refetch().catch((e: any) => console.log('Unknown Error', e));

  const rotateSecret = async (rotated: boolean) => {
    setConfirmingReset(false);
    setRotating(true);
    try {
      const r = await rotateWebhookSecret(VerdocsEndpoint.getDefault());
      setRevealed({secret: r.secret_key || '', rotated});
    } catch (e: any) {
      console.log('Error rotating webhook secret', e);
      toast.error(`Unable to reset the signing secret: ${e.response?.data?.error || e.message}`);
    }
    setRotating(false);
    refetch();
  };

  const handleRotateRequest = () => {
    if (webhook?.secret_key) {
      setConfirmingReset(true);
    } else {
      rotateSecret(false).catch((e: any) => console.log('Unknown Error', e));
    }
  };

  return (
    <div className="w-full max-w-[1100px] pb-10">
      <PageTitle>
        Webhooks
        <FlexFill />
        <HelpDialog title="Webhooks">
          <p>
            Webhooks are server-to-server notifications that may be sent to a URL of your choice when certain events occur within your
            organization. Webhook events are sent via POST requests with a JSON payload containing details about the event.
          </p>
          <p>
            If you are interested in using Webhooks but not yet ready to provide an endpoint of your own, we recommend the excellent{' '}
            <a href="https://webhook.site/" target="_blank" rel="noopener noreferrer">
              Webhook.site
            </a>
            &nbsp; tool for testing and development. This is a free service that provides a unique URL for testing Webhooks, with a modest
            monthly fee for more advanced features.
          </p>
          <p>
            The delivery log keeps each delivery for 90 days. When a delivery fails, we retry it automatically up to 12 times (so a
            failed delivery may still be retrying).
          </p>
        </HelpDialog>
      </PageTitle>

      {loading ? (
        <Spinner mode="dark" />
      ) : webhookQuery.isError && !notFound ? (
        <Card>
          <div className="text-[13px] text-gray-500">Unable to load the webhook settings. Please try again later.</div>
        </Card>
      ) : !webhook ? (
        <EmptyState title="No webhook endpoint" action={<Button label="Add an endpoint" onClick={() => setEditing(true)} />}>
          Instead of asking us for updates, tell us where to send them. We will POST a JSON payload to your URL whenever an envelope moves,
          a recipient acts, or a template changes.
        </EmptyState>
      ) : (
        <>
          <div className="mb-4">
            <SegmentedControl
              value={tab}
              onChange={setTab}
              options={[
                {key: 'endpoint', label: 'Endpoint'},
                {key: 'log', label: 'Delivery log'},
              ]}
            />
          </div>

          {tab === 'endpoint' && (
            <>
              <EndpointCard webhook={webhook} onEdit={() => setEditing(true)} />

              <Card>
                <SectionTitle>Verifying what you receive</SectionTitle>
                <div className="text-[13px] leading-normal text-gray-500">
                  Each delivery is a POST whose JSON body has <Code>id</Code>, <Code>event</Code>, <Code>created_at</Code>,{' '}
                  <Code>organization_id</Code>, and <Code>data</Code>. With HMAC, the <Code>x-webhook-signature</Code> header carries the
                  hex HMAC-SHA256 of <Code>JSON.stringify(data)</Code>, keyed with your signing secret. Ensure your endpoint responds
                  with a 2xx status code as soon as you have stored the payload - deliveries time out after 3 seconds.
                </div>
              </Card>
            </>
          )}

          {tab === 'log' && <WebhookDeliveryLog />}
        </>
      )}

      {editing && (
        <EditWebhookDialog
          webhook={webhook}
          rotating={rotating}
          onRotateSecret={handleRotateRequest}
          onClose={() => setEditing(false)}
          onSaved={(generatedSecret) => {
            setEditing(false);
            if (generatedSecret) {
              setRevealed({secret: generatedSecret, rotated: false});
            }
            refetch();
          }}
        />
      )}

      {revealed && <WebhookSecretDialog secret={revealed.secret} rotated={revealed.rotated} onClose={() => setRevealed(null)} />}

      {confirmingReset && (
        <OkCancelDialog
          title="Reset the signing secret?"
          okLabel="Reset secret"
          onOk={() => rotateSecret(true).catch((e: any) => console.log('Unknown Error', e))}
          onCancel={() => setConfirmingReset(false)}>
          <p className="text-[13px] leading-normal text-gray-500">
            We start signing deliveries with the new secret immediately, and an endpoint still checking the old one will reject them until
            you update it. This action cannot be undone.
          </p>
        </OkCancelDialog>
      )}
    </div>
  );
});
